/**
 * Weapons catalog — the 22 martial-arts weapons used in the GvG planner.
 *
 * Each entry's `icon` points at its martial-art path badge under
 * `assets/weapons/<sub_path>.png`; `slug` remains the canonical identifier.
 * Standard cards, filters, saved builds, and Discord raid images use that
 * badge. `artwork` holds the weapon-specific image under
 * `assets/weapons/<slug>.png` for the weapon selector only, where the path
 * badge is overlaid at the bottom center.
 * `role` drives card sorting + filter chips; `color` is the role-tinted accent
 * (tank / dps / heal) used for borders and accent strips on the weapon icon.
 *
 * The role/color values are the canonical source of truth for the planner's
 * weapon rendering.
 */

import { WeaponRole, WeaponRoleColor, WeaponSubPath, WeaponType } from '@/types/weapon'
import type { Weapon } from '@/types/weapon'

const WEAPON_ENTRIES: ReadonlyArray<Weapon> = [
  {
    slug: 'nameless_sword',
    name: 'Nameless Sword',
    cn: '无名剑',
    type: WeaponType.Sword,
    role: WeaponRole.Dps,
    color: WeaponRoleColor.Dps,
    subPath: WeaponSubPath.BellstrikeSplendor,
    icon: '/assets/weapons/bellstrike_splendor.png',
    artwork: '/assets/weapons/nameless_sword.png',
    description:
      'A foundational sword style prized for its mobility and high burst window.',
  },
  {
    slug: 'nameless_spear',
    name: 'Nameless Spear',
    cn: '无名枪',
    type: WeaponType.Spear,
    role: WeaponRole.Dps,
    color: WeaponRoleColor.Dps,
    subPath: WeaponSubPath.BellstrikeSplendor,
    icon: '/assets/weapons/bellstrike_splendor.png',
    artwork: '/assets/weapons/nameless_spear.png',
    description:
      'A stripped-down spear style prized for its mobility and high burst window.',
  },
  {
    slug: 'strategic_sword',
    name: 'Strategic Sword',
    cn: '积矩九剑',
    type: WeaponType.Sword,
    role: WeaponRole.Dps,
    color: WeaponRoleColor.Dps,
    subPath: WeaponSubPath.BellstrikeUmbra,
    icon: '/assets/weapons/bellstrike_umbra.png',
    artwork: '/assets/weapons/strategic_sword.png',
    description:
      'A chess-themed sword style that turns positioning into lethal counterattacks.',
  },
  {
    slug: 'heavenquaker_spear',
    name: 'Heavenquaker Spear',
    cn: '鸣金虹',
    type: WeaponType.Spear,
    role: WeaponRole.Dps,
    color: WeaponRoleColor.Dps,
    subPath: WeaponSubPath.BellstrikeUmbra,
    icon: '/assets/weapons/bellstrike_umbra.png',
    artwork: '/assets/weapons/heavenquaker_spear.png',
    description:
      'Thunder-charged spear strikes that chain lightning between grouped foes.',
  },
  {
    slug: 'thundercry_blade',
    name: 'Thundercry Blade',
    cn: '轰雷斩',
    type: WeaponType.MoBlade,
    role: WeaponRole.Tank,
    color: WeaponRoleColor.Tank,
    subPath: WeaponSubPath.StonesplitMight,
    icon: '/assets/weapons/stonesplit_might.png',
    artwork: '/assets/weapons/thundercry_blade.png',
    description:
      'A thunder-resonant blade that pairs high mobility with shockwave finishers.',
  },
  {
    slug: 'stormbreaker_spear',
    name: 'Stormbreaker Spear',
    cn: '裂石',
    type: WeaponType.Spear,
    role: WeaponRole.Dps,
    color: WeaponRoleColor.Dps,
    subPath: WeaponSubPath.StonesplitMight,
    icon: '/assets/weapons/stonesplit_might.png',
    artwork: '/assets/weapons/stormbreaker_spear.png',
    description:
      'A brutal polearm style built around seismic charges and armor-crushing lunges.',
  },
  {
    slug: 'phalanxbane_blade',
    name: 'Phalanxbane Blade',
    cn: '噬魂陌刀',
    type: WeaponType.MoBlade,
    role: WeaponRole.Dps,
    color: WeaponRoleColor.Dps,
    subPath: WeaponSubPath.StonesplitStrength,
    icon: '/assets/weapons/stonesplit_strength.png',
    artwork: '/assets/weapons/phalanxbane_blade.png',
    description:
      'A heavy blade forged for smashing shield walls and breaking enemy formations.',
  },
  {
    slug: 'snowparting_blade',
    name: 'Snowparting Blade',
    cn: '断雪刀',
    type: WeaponType.MoBlade,
    role: WeaponRole.Dps,
    color: WeaponRoleColor.Dps,
    subPath: WeaponSubPath.StonesplitStrength,
    icon: '/assets/weapons/stonesplit_strength.png',
    artwork: '/assets/weapons/snowparting_blade.png',
    description:
      'A frost-imbued cleaver that slows groups and amplifies follow-up damage.',
  },
  {
    slug: 'panacea_fan',
    name: 'Panacea Fan',
    cn: '明川药典',
    type: WeaponType.Fan,
    role: WeaponRole.Heal,
    color: WeaponRoleColor.Heal,
    subPath: WeaponSubPath.SilkbindDeluge,
    icon: '/assets/weapons/silkbind_deluge.png',
    artwork: '/assets/weapons/panacea_fan.png',
    description:
      'The signature healer fan — wide-radius heals, cleanses, and revival pulses.',
  },
  {
    slug: 'inkwell_fan',
    name: 'Inkwell Fan',
    cn: '青山執筆',
    type: WeaponType.Fan,
    role: WeaponRole.Dps,
    color: WeaponRoleColor.Dps,
    subPath: WeaponSubPath.SilkbindJade,
    icon: '/assets/weapons/silkbind_jade.png',
    artwork: '/assets/weapons/inkwell_fan.png',
    description:
      'A literati fan style that turns brushstroke calligraphy into shields and counters.',
  },
  {
    slug: 'soulshade_umbrella',
    name: 'Soulshade Umbrella',
    cn: '千香引魂蛊',
    type: WeaponType.Umbrella,
    role: WeaponRole.Heal,
    color: WeaponRoleColor.Heal,
    subPath: WeaponSubPath.SilkbindDeluge,
    icon: '/assets/weapons/silkbind_deluge.png',
    artwork: '/assets/weapons/soulshade_umbrella.png',
    description:
      'A phantom umbrella style that channels shade-spirits into sustained group heals and revival pulses.',
  },
  {
    slug: 'vernal_umbrella',
    name: 'Vernal Umbrella',
    cn: '春水碧',
    type: WeaponType.Umbrella,
    role: WeaponRole.Dps,
    color: WeaponRoleColor.Dps,
    subPath: WeaponSubPath.SilkbindJade,
    icon: '/assets/weapons/silkbind_jade.png',
    artwork: '/assets/weapons/vernal_umbrella.png',
    description:
      'A spring-rain umbrella style that detonates bloom-burst damage across clustered targets.',
  },
  {
    slug: 'everspring_umbrella',
    name: 'Everspring Umbrella',
    cn: '长风扇',
    type: WeaponType.Umbrella,
    role: WeaponRole.Dps,
    color: WeaponRoleColor.Dps,
    subPath: WeaponSubPath.BamboocutDust,
    icon: '/assets/weapons/bamboocut_dust.png',
    artwork: '/assets/weapons/everspring_umbrella.png',
    description:
      'A breezy umbrella style that whirls wide-arcing damage sweeps and kite pressure into every fight.',
  },
  {
    slug: 'infernal_twinblades',
    name: 'Infernal Twinblades',
    cn: '业火劫',
    type: WeaponType.DualBlades,
    role: WeaponRole.Dps,
    color: WeaponRoleColor.Dps,
    subPath: WeaponSubPath.BamboocutWind,
    icon: '/assets/weapons/bamboocut_wind.png',
    artwork: '/assets/weapons/infernal_twinblades.png',
    description:
      'Twin demon-forged blades traded for raw damage and relentless close-range pressure.',
  },
  {
    slug: 'mortal_rope_dart',
    name: 'Mortal Rope Dart',
    cn: '九曲惊魂枪',
    type: WeaponType.RopeDart,
    role: WeaponRole.Dps,
    color: WeaponRoleColor.Dps,
    subPath: WeaponSubPath.BamboocutWind,
    icon: '/assets/weapons/bamboocut_wind.png',
    artwork: '/assets/weapons/mortal_rope_dart.png',
    description:
      'A whip-and-dart hybrid for assassins who flow between burst damage and lock-down.',
  },
  {
    slug: 'unfettered_rope_dart',
    name: 'Unfettered Rope Dart',
    cn: '迷踪无影',
    type: WeaponType.RopeDart,
    role: WeaponRole.Dps,
    color: WeaponRoleColor.Dps,
    subPath: WeaponSubPath.BamboocutDust,
    icon: '/assets/weapons/bamboocut_dust.png',
    artwork: '/assets/weapons/unfettered_rope_dart.png',
    description:
      'A free-form rope dart that swaps between ranged kiting and melee lock-down.',
  },
  {
    slug: 'skygrasp_rope_dart',
    name: 'Skygrasp Rope Dart',
    cn: '千机索天',
    type: WeaponType.RopeDart,
    role: WeaponRole.Dps,
    color: WeaponRoleColor.Dps,
    subPath: WeaponSubPath.BamboocutKite,
    icon: '/assets/weapons/bamboocut_kite.png',
    artwork: '/assets/weapons/skygrasp_rope_dart.png',
    description:
      'A crowd-control rope dart that snares enemies at range and builds Heaven’s Will for its paired gauntlet finisher.',
  },
  {
    slug: 'heavenwill_gauntlets',
    name: 'Heavenwill Gauntlets',
    cn: '天志垂象',
    type: WeaponType.Gauntlets,
    role: WeaponRole.Dps,
    color: WeaponRoleColor.Dps,
    subPath: WeaponSubPath.BamboocutKite,
    icon: '/assets/weapons/bamboocut_kite.png',
    artwork: '/assets/weapons/heavenwill_gauntlets.png',
    description:
      'An aggressive gauntlet style that turns Heaven’s Will into close-range burst damage.',
  },
  {
    slug: 'skystrike_gauntlets',
    name: 'Skystrike Gauntlets',
    cn: '悬身拳法',
    type: WeaponType.Gauntlets,
    role: WeaponRole.Dps,
    color: WeaponRoleColor.Dps,
    subPath: WeaponSubPath.BamboocutDraught,
    icon: '/assets/weapons/bamboocut_draught.png',
    artwork: '/assets/weapons/skystrike_gauntlets.png',
    description:
      'A drunken boxing style that builds Binge and turns Inebriate into deceptive footwork and powerful strikes.',
  },
  {
    slug: 'riven_twinblades',
    name: 'Riven Twinblades',
    cn: '断水双诀',
    type: WeaponType.DualBlades,
    role: WeaponRole.Dps,
    color: WeaponRoleColor.Dps,
    subPath: WeaponSubPath.BamboocutDraught,
    icon: '/assets/weapons/bamboocut_draught.png',
    artwork: '/assets/weapons/riven_twinblades.png',
    description:
      'A relentless dual-blade style that shares Binge with Skystrike Gauntlets and spends drunkenness on burst attacks.',
  },
  {
    slug: 'museflow_waist_drum',
    name: 'Museflow Waist Drum',
    cn: '文动霓裳',
    type: WeaponType.WaistDrum,
    role: WeaponRole.Dps,
    color: WeaponRoleColor.Dps,
    subPath: WeaponSubPath.SilkbindWings,
    icon: '/assets/weapons/silkbind_wings.png',
    artwork: '/assets/weapons/museflow_waist_drum.png',
    description:
      'A ribbon-and-drum style that combines rhythmic attacks, crowd control, and protection for nearby allies.',
  },
  {
    slug: 'willowbend_fan',
    name: 'Willowbend Fan',
    cn: '杏游草野',
    type: WeaponType.Fan,
    role: WeaponRole.Dps,
    color: WeaponRoleColor.Dps,
    subPath: WeaponSubPath.SilkbindWings,
    icon: '/assets/weapons/silkbind_wings.png',
    artwork: '/assets/weapons/willowbend_fan.png',
    description:
      'A flowing fan style that controls enemy spacing and supports allies with wind fields and swift follow-up attacks.',
  },
]

export const WEAPONS: ReadonlyArray<Weapon> = WEAPON_ENTRIES

export const WEAPONS_BY_SLUG: ReadonlyMap<string, Weapon> = new Map(
  WEAPON_ENTRIES.map((w) => [w.slug, w] as const),
)

/**
 * Public asset path of the visible fallback used when a slug is unknown to
 * the catalog. Rendered as a red dashed-bordered "MISSING" tile so a typo
 * (or a stale `localStorage` snapshot) is obvious in development instead of
 * silently rendering a 1×1 transparent pixel. Lives next to the other
 * weapon glyphs under `public/assets/weapons/` for consistency with the
 * rest of the asset pipeline.
 */
export const MISSING_WEAPON_ICON = '/assets/weapons/missing_icon.svg'

/** Returns the public asset path for the standard martial-art path badge. Unknown slugs fall back to a visible "MISSING" placeholder so typos are obvious in development. */
export function weaponIcon(slug: string): string {
  const icon = WEAPONS_BY_SLUG.get(slug)?.icon
  return icon ?? MISSING_WEAPON_ICON
}
